import { MortgageEntity } from '@entities/mortgage/mortgage.reducer';
import { ProductType } from '../api/dsm-types';
import { ProductModel } from '@entities/product/product.model';
import { BasicAddressEntity } from '@entities/address/address.entity';
import { SaeAccountPayMethod } from '../api/request/setup-account-experience-request.model';
import { PolicyHolderEntity } from '@entities/policyholder/policyholder.entity';
import { Nullable } from '@shared/utils/type.utils';

export interface PayBindPageModel {
  products: ProductModel[];
  paymentTypes: PaymentType[];
  disclosures?: BillingDisclosuresModel;
}

export type PaymentPlanType =
  | 'ESCROW'
  | 'PAY IN FULL'
  | 'MONTHLY DIRECT'
  | 'MONTHLY EFT'
  | 'MONTHLY RECURRING BANKCARD';

export interface PaymentType {
  paymentPlan: PaymentPlanType;
  imageUrl?: string;
  products: ProductBillingDetail[];
  downPaymentDetails?: DownPaymentDetails;
  paymentMethods?: PaymentMethod[];
  errorMessage?: string;
}
export interface ProductBillingDetail {
  productType: ProductType;
  name: string;
  totalPremium: number;
  premiumTerm: number;
  quoteId: string;
  imageUrl: string;
  mortgage?: MortgageEntity;
  effectiveDate?: string;
}

export interface DownPaymentDetails {
  fees: Fee[];
  surcharges: Surcharge[];
  downPaymentAmount?: number;
  fullPayAmount?: number;
  nextBillingDate?: string;
  nextBillingAmount?: number;
}

export interface Fee {
  description: string;
  amount: number;
}
export interface Surcharge extends Fee {}

export type PaymentMethodType =
  | 'Bank Account'
  | 'Credit Debit Card'
  | 'Agency Sweep'
  | 'Escrow';

export type PaymentOccurrenceType =
  | 'Down Payment'
  | 'Recurring Payment'
  | 'Both';

export interface PaymentMethod {
  id?: 'paymentMethod-1' | 'paymentMethod-2';
  paymentMethod?: PaymentMethodType;
  occurrence?: PaymentOccurrenceType;
  agencySweep?: AgencySweep;
  bankAccount?: BankAccount;
  bankCard?: BankCard;
  accountHolder?: PersonOnAccount;
  dayOfMonthDue?: string; // Two digits
}

export interface AgencySweep {
  agencySweepPaymentType?: string;
  checkNumber?: number;
}

export interface BankAccount {
  routingNumber?: string;
  bankName?: string;
  bankAccountNumber?: string;
  monthlyPaymentDay?: string;
}
export interface BankCard {
  cardType?: string;
  cardNumber?: string;
  expirationDate?: string;
  cvv?: string;
  cid?: string;
  cardLastFour?: string;
  profileId?: string;
}

export interface PersonOnAccount {
  name?: NameOnAccount;
  address?: BasicAddressEntity;
}

export interface NameOnAccount {
  firstName?: string;
  middleName?: string;
  lastName?: string;
  suffix?: string;
}

export interface AddressOnAccount {
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export function saeAccountPayMethodForPaymentPlanType(
  type: PaymentPlanType
): SaeAccountPayMethod {
  switch (type) {
    case 'ESCROW':
      return 'Third Party';
    case 'PAY IN FULL':
      return 'Direct Bill';
    case 'MONTHLY DIRECT':
      return 'Direct Bill';
    case 'MONTHLY EFT':
      return 'Recurring EFT';
    case 'MONTHLY RECURRING BANKCARD':
      return 'Recurring BankCard';
  }
  return 'Direct Bill';
}

export function saePayPlanForPaymentPlanType(type: PaymentPlanType): string {
  // sommea1 2025-07-07: OAS does not list the values for payPlan. Assuming it's just these two.
  switch (type) {
    case 'ESCROW':
      return 'FULL PAYMENT PLAN';
    case 'PAY IN FULL':
      return 'FULL PAYMENT PLAN';
    case 'MONTHLY DIRECT':
      return 'COMBINED MONTHLY';
    case 'MONTHLY EFT':
      return 'COMBINED MONTHLY';
    case 'MONTHLY RECURRING BANKCARD':
      return 'COMBINED MONTHLY';
  }
  return 'COMBINED MONTHLY';
}

export interface BillingDisclosuresModel {
  namedInsured?: Nullable<PolicyHolderEntity>;
  isSurchargeDisclosureAvailable?: boolean;
  selectedSurchargeAgreement?: boolean;
  requireSurchargeAgreement?: boolean;
  isPaymentAuthorizationAvailable?: boolean;
  selectedPaymentAuthorization?: boolean;
  requirePaymentAuthorization?: boolean;
}
